import type { ParameterLibraryType } from '@malaberg/shared';
/**
 * EntryHistoryExpander — T26.
 *
 * Shows a collapsible list of historical versions for a library entry.
 * Each row shows: version number, created_at, displayName snippet.
 * "Revert to this version" button calls librariesApi.revert (visible only when canWrite).
 */
import { Alert, Badge, Box, Button, Collapse, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { librariesApi } from '../../../api/endpoints/libraries';

interface HistoryEntry {
  id: string;
  version: number;
  displayName: string;
  createdAt: string;
  isCurrent: boolean;
}

interface EntryHistoryExpanderProps {
  /** The concept ID (shared across versions). */
  entryId: string;
  type: ParameterLibraryType;
  canWrite: boolean;
  /** Called after a successful revert so the parent can refresh the list. */
  onReverted?: () => void;
}

export default function EntryHistoryExpander({
  entryId,
  type,
  canWrite,
  onReverted,
}: EntryHistoryExpanderProps) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reverting, setReverting] = useState<number | null>(null);

  async function loadHistory() {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = (await librariesApi.getVersions(type, entryId)) as {
        ok: true;
        versions?: HistoryEntry[];
        items?: HistoryEntry[];
      };
      // Show older (non-current) versions only — the current version is shown in the detail pane
      setHistory((res.versions ?? res.items ?? []).filter((v) => !v.isCurrent));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history.');
    } finally {
      setLoading(false);
    }
  }

  function handleToggle() {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && history.length === 0) {
      void loadHistory();
    }
  }

  async function handleRevert(version: number) {
    setReverting(version);
    setError(null);
    try {
      await librariesApi.revert(type, entryId, version);
      // Refresh history after revert
      await loadHistory();
      onReverted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revert.');
    } finally {
      setReverting(null);
    }
  }

  return (
    <Stack gap="xs" data-testid="library-history-section">
      <Button size="xs" variant="subtle" onClick={handleToggle} data-testid="history-toggle-btn">
        {open ? 'Hide history' : 'Show history'}
      </Button>

      <Collapse in={open}>
        <Stack gap="xs">
          {error && <Alert color="red">{error}</Alert>}

          {loading && (
            <Text size="xs" c="dimmed">
              Loading…
            </Text>
          )}

          {!loading && history.length === 0 && (
            <Text size="xs" c="dimmed" data-testid="history-empty">
              No prior versions.
            </Text>
          )}

          {history.map((row, idx) => (
            <Box
              key={`${row.id}-v${row.version}`}
              p="xs"
              style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 4 }}
              data-testid={`history-row-${idx}`}
            >
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Group gap="xs">
                    <Badge size="xs" variant="outline" color="gray">
                      v{row.version}
                    </Badge>
                    <Text size="xs" style={{ wordBreak: 'break-all' }} truncate>
                      {row.displayName}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {new Date(row.createdAt).toLocaleString()}
                  </Text>
                </Stack>

                {canWrite && (
                  <Button
                    size="xs"
                    variant="default"
                    loading={reverting === row.version}
                    disabled={reverting !== null}
                    onClick={() => {
                      void handleRevert(row.version);
                    }}
                    data-testid={`history-revert-btn-${idx}`}
                  >
                    Revert to this version
                  </Button>
                )}
              </Group>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}
